import { useEffect, useState, useCallback } from 'react';
import { getActivityLogs } from 'api/activityLog';
import { createCase, updateCase } from 'api/cases';
import searchCustomers from 'api/cases/searchCustomers';
import { CustomerOption } from 'api/cases/searchCustomers';
import { filterActivityLogs } from 'containers/pages/activityLogManagement/libs';
import { useNotificationProvider } from 'hooks';
import { ActivityLog, ActivityLogFormData } from 'containers/pages/activityLogManagement/types';
import { CasePayload, CaseUpdatePayload } from 'api/cases/types';

function useActivityLogManagement() {
  const { notifySuccess, notifyError } = useNotificationProvider();

  const [isLoading, setIsLoading] = useState(false);
  const [isOpenForm, setIsOpenForm] = useState(false);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [searchedLogs, setSearchedLogs] = useState<ActivityLog[]>([]);
  const [searchInput, setSearchInput] = useState('');

  // Customer filter
  const [customerFilter, setCustomerFilter] = useState<CustomerOption | null>(null);
  const [customerFilterOptions, setCustomerFilterOptions] = useState<CustomerOption[]>([]);

  const onToggleForm = () => {
    setIsOpenForm((prev) => !prev);
  };

  const onSearchInput = (input: string) => {
    setSearchInput(input);
    if (!input) {
      setSearchedLogs(activityLogs);
    } else {
      const filtered = filterActivityLogs(input, activityLogs);
      setSearchedLogs(filtered);
    }
  };

  const onResetSearch = () => {
    setSearchInput('');
    setSearchedLogs(activityLogs);
  };

  const onCustomerFilterChange = useCallback((_e: unknown, val: CustomerOption | null) => {
    setCustomerFilter(val);
  }, []);

  const onCustomerFilterSearch = useCallback((_e: unknown, val: string) => {
    searchCustomers(val).then(setCustomerFilterOptions).catch(() => {});
  }, []);

  const onCustomerFilterOpen = useCallback(() => {
    searchCustomers('').then(setCustomerFilterOptions).catch(() => {});
  }, []);

  const onRefreshLogs = useCallback(async (customerId?: number | null) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const filters: any = {};
      if (customerId) {
        filters.CustomerId = customerId;
      }
      const result = await getActivityLogs(filters);
      const data: ActivityLog[] = result?.Data || [];
      setActivityLogs(data);
      setSearchedLogs(data);
      setSearchInput('');
    } catch (error) {
      console.error('[useActivityLogManagement] Refresh failed:', error);
      notifyError('Failed to refresh activity logs');
    }
  }, [notifyError]);

  const onSaveActivityLog = useCallback(
    async (formData: ActivityLogFormData, isEdit: boolean) => {
      try {
        const payload: CasePayload = {
          Title: formData.Title,
          Description: formData.Description || undefined,
          StatusComment: formData.StatusComment || undefined,
          StatusId: formData.StatusId!,
          CustomerId: formData.CustomerId!,
          ConsultantId: formData.ConsultantId!,
          PrimaryContactId: formData.PrimaryContactId || undefined,
          GroupId: formData.GroupId || undefined,
          ProjectId: formData.ProjectId || undefined,
          DeliverableId: formData.DeliverableId || undefined,
          Private: formData.Private,
          IpRestricted: formData.IpRestricted,
        };

        if (isEdit && formData.Id) {
          const updatePayload: CaseUpdatePayload = { ...payload, Id: formData.Id };
          await updateCase(updatePayload);
          notifySuccess('Activity log updated successfully');
        } else {
          await createCase(payload);
          notifySuccess('Activity log created successfully');
          setIsOpenForm(false);
        }
        await onRefreshLogs(customerFilter?.Id);
      } catch (error) {
        console.error('[useActivityLogManagement] Save failed:', error);
        notifyError('Failed to save activity log. Please try again.');
      }
    },
    [notifySuccess, notifyError, onRefreshLogs, customerFilter],
  );

  // Load activity logs when customer filter changes
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const filters: any = {};
        if (customerFilter?.Id) {
          filters.CustomerId = customerFilter.Id;
        }
        const result = await getActivityLogs(filters);
        const data: ActivityLog[] = result?.Data || [];
        setActivityLogs(data);
        setSearchedLogs(data);
        setSearchInput('');
      } catch (error) {
        console.error('[useActivityLogManagement] Load failed:', error);
        notifyError('Failed to load activity logs');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [customerFilter, notifyError]);

  const state = {
    isLoading,
    isOpenForm,
    searchedLogs,
    searchInput,
    customerFilter,
    customerFilterOptions,
  };

  const handler = {
    onToggleForm,
    onSearchInput,
    onResetSearch,
    onCustomerFilterChange,
    onCustomerFilterSearch,
    onCustomerFilterOpen,
    onSaveActivityLog,
  };

  return { state, handler };
}

export default useActivityLogManagement;
